import { motion, AnimatePresence, useInView } from 'framer-motion';
import { StackedContent, MotionImage, useSanitizedTranslation, TextBlock } from 'ui';
import { useState, useRef, useEffect } from 'react';
import { IImage } from 'ui';
import Image from 'next/image';

export interface Testimonial {
  copy: string;
  name: string;
}

export interface TestimonialsProps {
  bg: string;
  image: string;
  testimonials: Testimonial[];
  title: string;
}

export function Testimonials ({bg='white', image, testimonials, title}: TestimonialsProps) {

    const { t } = useSanitizedTranslation();

  const [currentItem, setCurrentItem] = useState(0)

  const changeItem = (index: number) => {
      const newItem = index < testimonials.length ? index : 0
      setCurrentItem(newItem)
  }

  useEffect(()=> {
      const newItem = currentItem + 1
      const timeout = setTimeout(() => {
          changeItem(newItem)
      }, 4000);
      return () => clearTimeout(timeout);
  }, [currentItem])

  return(
      <motion.section className='relative w-full h-full flex flex-col-reverse lg:flex-row items-center justify-center py-20' style={{background: bg}}>
          <motion.div 
            className='w-full lg:w-[55%] h-full pt-0 pb-12 lg:p-0'
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: '-30% 0% -30% 0%' }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
            <Image
              src={image} 
              alt="tv"
              width={2000} 
              height={1125}
            />            
          </motion.div>
          <div className='w-full max-w-[700px] lg:maxw-none lg:w-[45%] h-full flex items-center justify-center p-10 lg:p-0 mx-auto lg:ml-0 lg:mr-10 xl:mr-16'>
              <AnimatePresence mode="wait">
                  <motion.div 
                      className='w-full h-[70vh] bg-white px-10 py-28 rounded-lg flex flex-col items-center justify-between text-center gap-10'
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.5 }}
                  >
                      <h2>{t(title)}</h2>
                      <motion.p
                          key={currentItem}
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -20 }}
                          transition={{ duration: 0.5 }}
                      >“{t(testimonials[currentItem].copy)}”</motion.p>
                      <div className='flex flex-col gap-10 items-center justify-center'>
                          <motion.p
                              key={currentItem}
                              initial={{ opacity: 0, x: 20 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: -20 }}
                              transition={{ duration: 0.5 }}
                          >{t(testimonials[currentItem].name)}</motion.p>
                          <div className='w-full h-max flex flex-row items-center justify-center gap-5'>
                              {testimonials.map((item, index) => (
                                  <motion.div
                                      key={`${index}-dot-${item}`}
                                      initial={{ opacity: 0.5 }}
                                      animate={{ opacity: currentItem === index ? 1 : 0.5 }}
                                      transition={{ duration: 0.5 }}
                                      className={`w-3 h-3 rounded-full cursor-pointer hover:scale-110 bg-black`}
                                      onClick={()=>changeItem(index)}
                                  ></motion.div>
                              ))}
                          </div>
                      </div>
                  </motion.div>
              </AnimatePresence>
          </div>
      </motion.section>
  )
}
